/**
 * AuthBrandPanel — shared NDORFlow brand hero for /login and /signup.
 *
 * RC 1.45.HARMONISE — candidate-first hero aligned to the marketing
 * site's RC 1.44.W++ positioning:
 *   • H2: "Every application, backed by evidence."
 *   • Italic subline (gold): "Apply smarter. Interview stronger."
 *
 * The "For Candidates / For Employers" value cards remain — the
 * ecosystem still serves both sides, and /employers is live on the
 * marketing site. Trust strip ("Premium · Audit-ready · Private")
 * unchanged.
 *
 * Two visual variants:
 *   • variant="desktop" — full-bleed left panel for ≥ lg viewports.
 *     Caller mounts inside an `<aside class="hidden lg:flex">` wrapper.
 *   • variant="mobile"  — compact stacked block for < lg viewports.
 *     Caller mounts above the auth form inside a `<div class="lg:hidden">`
 *     wrapper. Sized to keep the form in view above the keyboard on
 *     390px-wide handsets.
 */

import BrandLogo from '@/components/BrandLogo';

function ValueCards({ compact = false }) {
  const cardClass = compact
    ? 'rounded-lg border border-ndor-edge bg-ndor-surface/60 p-3'
    : 'rounded-xl border border-ndor-edge bg-ndor-surface/60 p-5';
  const labelClass = 'text-[10px] uppercase tracking-[0.22em] text-ndor-gold font-mono-ui';
  const listClass = compact
    ? 'mt-2 space-y-1 text-[12px] text-ndor-ink-secondary leading-snug'
    : 'mt-3 space-y-1.5 text-sm text-ndor-ink-secondary leading-relaxed';
  return (
    <div className={compact ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-2 gap-5'}>
      <div className={cardClass} data-testid="auth-brand-candidates-card">
        <p className={labelClass}>For Candidates</p>
        <ul className={listClass}>
          <li>Track roles</li>
          <li>Analyse fit</li>
          <li>Generate tailored CVs &amp; cover letters</li>
          <li>Assemble stronger application packs</li>
        </ul>
      </div>
      <div className={cardClass} data-testid="auth-brand-employers-card">
        <p className={labelClass}>For Employers</p>
        <ul className={listClass}>
          <li>Post roles</li>
          <li>Screen applicants</li>
          <li>Rank candidates with evidence</li>
          <li>Run structured hiring workflows</li>
        </ul>
      </div>
    </div>
  );
}

export default function AuthBrandPanel({ variant = 'desktop' }) {
  if (variant === 'mobile') {
    return (
      <div
        data-testid="auth-brand-panel-mobile"
        className="lg:hidden relative brand-hex-grid rounded-2xl border border-ndor-edge/70 bg-ndor-canvas/40 px-5 py-7 sm:px-7 sm:py-9 mb-6 sm:mb-8 overflow-hidden"
      >
        <div className="relative z-[1] flex flex-col gap-5 sm:gap-6">
          {/* Brand block — centered on mobile so it reads as a hero. */}
          <div className="flex flex-col items-center text-center gap-3">
            <BrandLogo variant="symbol" size={64} className="sm:hidden" />
            <BrandLogo variant="symbol" size={80} className="hidden sm:block" />
            <div className="space-y-1">
              <p className="text-[28px] sm:text-[32px] font-serif-display font-semibold text-ndor-ink tracking-tight leading-none">
                NDOR<span className="text-gold-accent">Flow</span>
              </p>
              <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.24em] text-ndor-ink-secondary font-mono-ui">
                Decision Intelligence
              </p>
            </div>
          </div>

          {/* Headline + subline — candidate-first, matches marketing site. */}
          <div className="text-center space-y-1.5">
            <h2 className="text-[20px] sm:text-[22px] font-serif-display font-semibold text-ndor-ink leading-tight tracking-tight">
              Every application, backed by evidence.
            </h2>
            <p className="text-[15px] sm:text-base font-serif-display italic text-gold-accent leading-snug">
              Apply smarter. Interview stronger.
            </p>
          </div>

          {/* Value cards — kept side-by-side; the wrapping rounded
              padding compresses well on 390px and scales up cleanly. */}
          <ValueCards compact />

          {/* Trust tagline */}
          <div className="flex items-center justify-center gap-3 pt-1">
            <span className="inline-block h-[1px] w-8 bg-ndor-gold" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-ndor-ink-secondary font-mono-ui">
              Premium · Audit-ready · Private
            </span>
            <span className="inline-block h-[1px] w-8 bg-ndor-gold" />
          </div>
        </div>
      </div>
    );
  }

  // variant === 'desktop'
  return (
    <div
      data-testid="auth-brand-panel-desktop"
      className="absolute inset-0 flex items-center px-16 xl:px-24 py-12 overflow-y-auto"
    >
      <div className="relative z-[1] w-full max-w-2xl space-y-10">
        {/* Brand block — the ONLY place "Decision Intelligence" appears
            on the desktop hero. */}
        <div className="flex items-center gap-5">
          <BrandLogo variant="symbol" size={88} />
          <div className="leading-none">
            <p className="text-5xl font-serif-display font-semibold tracking-tight text-ndor-ink">
              NDOR<span className="text-gold-accent">Flow</span>
            </p>
            <p className="text-[11px] uppercase tracking-[0.28em] text-ndor-ink-secondary font-mono-ui mt-3">
              Decision Intelligence
            </p>
          </div>
        </div>

        {/* Hero headline — candidate-first, matches marketing H1. */}
        <div className="space-y-3">
          <h2 className="text-[34px] xl:text-[40px] font-serif-display font-semibold text-ndor-ink leading-[1.08] tracking-tight">
            Every application, backed by evidence.
          </h2>
          <p className="text-xl xl:text-2xl font-serif-display italic text-gold-accent leading-snug">
            Apply smarter. Interview stronger.
          </p>
          <p className="text-sm xl:text-[15px] text-ndor-ink-secondary leading-relaxed max-w-lg pt-1">
            NDORFlow helps you apply with the rigour employers respect.
            Evidence-led fit analysis, tailored CVs, and application
            packs — defensible line by line.
          </p>
        </div>

        <ValueCards />

        <div className="flex items-center gap-3 pt-1">
          <span className="inline-block h-[1px] w-10 bg-ndor-gold" />
          <span className="text-[11px] uppercase tracking-[0.25em] text-ndor-ink-secondary font-mono-ui">
            Premium. Audit-ready. Private.
          </span>
        </div>
      </div>
    </div>
  );
}
